import { API_BASE_URL } from '../config';
import { getAccessToken } from '../libs/auth/keycloakInstance';
import { mapApiError } from './errors/errorMapper';
import { originId } from '../libs/events/originId';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface HttpRequestOptions<TBody = unknown> {
    method?: HttpMethod;
    body?: TBody;
    headers?: HeadersInit;
    signal?: AbortSignal;
}

export interface HttpClient {
    get<T>(url: string, options?: Omit<HttpRequestOptions, 'method'>): Promise<T>;
    post<T, B>(url: string, body: B, options?: Omit<HttpRequestOptions<B>, 'method' | 'body'>) : Promise<T>;
    postForm<T>(url: string, body: FormData, options?: Omit<HttpRequestOptions<FormData>, 'method' | 'body'>): Promise<T>;
    put<T, B>(url: string, body: B, options?: Omit<HttpRequestOptions<B>, 'method' | 'body'>) : Promise<T>;
    patch<T, B>(url: string, body: B, options?: Omit<HttpRequestOptions<B>, 'method' | 'body'>): Promise<T>;
    delete<T>(url: string, options?: Omit<HttpRequestOptions, 'method'>): Promise<T>;
}

export const createHttpClient = (getAccessToken: () => Promise<string | undefined>): HttpClient => {

    const request = async <TResponse, TBody = unknown>(
        url: string,
        options: HttpRequestOptions<TBody> = {}
    ): Promise<TResponse> => {   

        const headers = new Headers(options.headers);
        headers.set('X-Origin-Id', originId);

        const isFormData = options.body instanceof FormData;
        if (!isFormData) {
            headers.set('Content-Type', 'application/json');
        }
        const token = await getAccessToken?.();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }

        const response = await fetch(`${API_BASE_URL}${url}`, {
            method: options.method ?? 'GET',
            headers,
            body: isFormData ? (options.body as FormData) : (options.body ? JSON.stringify(options.body) : undefined),
            signal: options.signal,
        });

        if (!response.ok) {
            const errorBody = await safeParseJson(response);
            throw mapApiError(errorBody);
        }

        return safeParseJson<TResponse>(response);
    }

    return {
        get: <T>(url: string, options?: Omit<HttpRequestOptions, 'method'>) =>
            request<T>(url, { ...options, method: 'GET' }),

        post: <T, B>(url: string, body: B, options?: Omit<HttpRequestOptions<B>, 'method' | 'body'>) =>
            request<T, B>(url, { ...options, method: 'POST', body }),

        postForm: <T>(url: string, body: FormData, options?: Omit<HttpRequestOptions<FormData>, 'method' | 'body'>) =>
            request<T, FormData>(url, { ...options, method: 'POST', body }),

        put: <T, B>(url: string, body: B, options?: Omit<HttpRequestOptions<B>, 'method' | 'body'>) =>
            request<T, B>(url, { ...options, method: 'PUT', body }),

        patch: <T, B>(url: string, body: B, options?: Omit<HttpRequestOptions<B>, 'method' | 'body'>) =>
            request<T, B>(url, { ...options, method: 'PATCH', body }),

        delete: <T>(url: string, options?: Omit<HttpRequestOptions, 'method'>) =>
            request<T>(url, { ...options, method: 'DELETE' }),
    };    
}


const safeParseJson = async <T>(response: Response): Promise<T> => {
    const text = await response.text();
    return text ? JSON.parse(text) : (undefined as T);
}

export const http = createHttpClient(getAccessToken);